<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: ProtectedComponent
  Reason: Trace detail panel layout. Renders a presentational summary card,
          the reusable variable store matrix, the read-only flow canvas with
          the same bindings the live designer uses, AND — TM-1000001563 — the
          extracted reusable step-detail sidebar + flow-I/O detail panel so
          step / START / ENDE clicks on the canvas open the same surfaces as
          in the live test runner. TM-1000001563 also adds nested sub-flow
          drill-in: SUB_FLOW step clicks swap the canvas to the child trace's
          level, the execution tree panel renders the full lazy-loaded
          hierarchy, and a "Sub-Flow: ..." chip with a "Zurück" button
          appears whenever the canvas is below the root.
-->
<!-- TM-1000001563: `-ml-3 -mr-3` cancels the parent route container's
     horizontal padding (`p-3`, 1rem each side) so the panel content uses
     the full available width. Symmetric on both sides keeps the layout
     centered. -->
<div class="grid m-0 -mt-4 -ml-3 -mr-3">

    <!-- Trace summary card -->
    <!-- TM-1000001563: column wrapper aligned to `col-12 p-0` so the
         summary card spans the same width as the variable matrix and the
         canvas panel below. The previous `col-12` default carried the
         PrimeFlex grid gutter (0.5rem each side) which made the summary
         card visibly narrower than its siblings. `mb-2` adds a small
         visible gap between the summary header and the variable matrix
         below it so it reads as the panel's header section. -->
    <div class="col-12 p-0 mb-2">
        @if (trace) {
            <mvs-lg-flow-trace-summary [trace]="trace"></mvs-lg-flow-trace-summary>
        } @else {
            <div class="p-3 text-color-secondary">
                Trace wird geladen...
            </div>
        }
    </div>

    <!-- Variable store matrix — NOT gated on `trace` so the panel stays
     visible even if the header load is still in-flight or returns empty. -->
    <!-- TM-1000001563: forward declared [flowImports]/[flowExports] so the
         variable matrix classifies rows as IMPORT (sign-in) / EXPORT
         (sign-out) instead of falling back to PROCESSING for every row.
         TM-1000001563 (Issue 5): the matrix is now driven by the per-level
         level* fields instead of the root trace fields — when the user
         drills into a SUB_FLOW step the matrix follows the active canvas
         level (resolving the persisted child LogicFlowTrace's flow IO +
         step traces). The declared imports/exports also use the per-level
         current* lists (already maintained by applyFlowDefinitionToCurrent)
         so the matrix's IMPORT/EXPORT classification reflects the contract
         of the active flow level, not the root flow. On root level
         level*/current* both mirror the trace's root data. -->
    <div class="col-12 p-0">
        <mvs-lg-flow-variable-store-panel
                [flowName]="matrixFlowName"
                [traceFlowInputs]="levelFlowInputs"
                [traceStepTraces]="levelStepTraces"
                [traceFlowOutputs]="levelFlowOutputs"
                [flowImports]="currentFlowImports"
                [flowExports]="currentFlowExports"
                [startExpanded]="false">
        </mvs-lg-flow-variable-store-panel>
    </div>

    <!-- Read-only flow canvas — renders the stored trace on the same
         designer component used by the live runner. Gated on currentFlowSteps so
         it only appears once the active level's flow definition has been loaded. -->
    @if (currentFlowSteps?.length) {
        <div class="col-12 p-0  ">
            <p-panel header="Flow-Verlauf" [toggleable]="true">
                <!-- TM-1000001563: full read-only mirror of the test-runner
                     canvas bindings — uses the per-level current* fields so
                     the canvas reflects the active sub-flow level on
                     drill-in. The variable-store panel above keeps using the
                     root-level fields so summary/matrix never lie about
                     which trace is open. -->
                <div class="flex flex-row gap-3 align-items-stretch w-full">
                    <!-- TM-1000001563: relative wrapper so the absolute-
                         positioned step-detail sidebar / flow-I/O panel /
                         sub-flow chip / execution-tree panel can float on
                         top of the canvas (matches the test-runner pattern). -->
                    <div class="flex-1 min-w-0 relative">
                        <!-- TM-1000001563: sub-flow drill-in chip — MS Teams-
                             style pill, top-center of canvas. Visible only
                             while the canvas is below the root level. The
                             "Zurück" button pops the parent trace and
                             navigates back one level.
                             TM-1000001563 (Issue 5): when the resolved
                             child LogicFlowTrace is missing for the
                             active sub-flow level, surface an additional
                             warning row below the chip explaining that
                             no detailed trace protocol is available. -->
                        @if (isOnSubFlow) {
                            <div class="lg-subflow-chip-overlay">
                                <div class="lg-subflow-chip-info">
                                    <i class="fa-regular fa-diagram-subtask"></i>
                                    <span>Sub-Flow: {{ currentFlowName }}</span>
                                </div>
                                <button
                                    type="button"
                                    class="lg-subflow-chip-action"
                                    pTooltip="Zurück zum übergeordneten Flow"
                                    tooltipPosition="bottom"
                                    (click)="onNavigateUpOneLevel()">
                                    <i class="fa-regular fa-arrow-left"></i>
                                    <span>Zurück</span>
                                </button>
                                @if (levelTraceMissing) {
                                    <p-tag
                                            severity="warn"
                                            icon="fa-regular fa-triangle-exclamation"
                                            value="Kein detailliertes Trace-Protokoll für diesen Sub-Flow verfügbar"
                                            pTooltip="Der Sub-Flow hat keinen eigenen LogicFlowTrace-Eintrag. Die Variablenmatrix zeigt die Eingabe- und Ausgabewerte aus dem übergeordneten Trace-Schritt; der Logik-Zähler ist deaktiviert.">
                                    </p-tag>
                                }
                            </div>
                        }

                        <!-- TM-1000001563: executionOverlay now driven off
                             the per-level state. Root level always has a
                             trace (levelTraceMissing=false) so the overlay
                             is ON. Sub-flow with a persisted child trace:
                             applyResolvedLevelTrace() sets
                             levelTraceMissing=false and populates
                             levelStepTraces — overlay ON, executed steps
                             bright, others blurred. Sub-flow with NO child
                             trace persisted (legacy data):
                             applyMissingLevelTrace() sets
                             levelTraceMissing=true — overlay OFF, canvas
                             renders static structure without faking
                             execution data. The variable matrix + counter
                             widget below the canvas already follow the
                             active level via the `level*` fields. -->
                        <lg-flow-designer
                            [steps]="currentFlowSteps"
                            [flowId]="currentFlow?.id ?? logicFlow?.id"
                            [layoutJson]="currentFlow?.layoutJson ?? logicFlow?.layoutJson"
                            [resolvedSteps]="currentResolvedSteps"
                            [overrideMap]="currentOverrideMap"
                            [readonly]="true"
                            [previewMode]="true"
                            [executionOverlay]="!levelTraceMissing"
                            [animateExecution]="false"
                            [stepTraces]="levelStepTraces"
                            [flowInputs]="currentFlowInputs"
                            [flowOutputs]="currentFlowOutputs"
                            [flowImports]="currentFlowImports"
                            [flowExports]="currentFlowExports"
                            [declarationsExist]="currentDeclarationsExist"
                            [callingStepIdsForAlias]="currentCallingStepIdsForAlias"
                            (declarationToggleClicked)="toggleDeclarationsView()"
                            (stepClicked)="onStepClicked($event)"
                            (stepDefClicked)="onSelectedStepDef($event)"
                            (flowIoClicked)="onFlowIoClicked($event)">
                        </lg-flow-designer>

                        <!-- TM-1000001563: extracted flow-I/O detail panel —
                             opens for START / ENDE clicks on the read-only
                             canvas, mirrors the live designer surface. -->
                        <mvs-lg-flow-io-detail-panel
                            [selection]="selectedFlowIo"
                            (close)="closeFlowIoDetail()">
                        </mvs-lg-flow-io-detail-panel>

                        <!-- TM-1000001563: extracted step-detail sidebar —
                             same surface the test runner now hosts. The
                             panel stays read-only via the canvas's
                             [readonly]="true"; the sidebar's editor /
                             mapping widgets surface their data without
                             allowing edits. -->
                        @if (sidebarOpen) {
                            <mvs-lg-flow-step-detail-sidebar
                                [stepTrace]="selectedStepTrace"
                                [stepDef]="selectedStepDef"
                                [mode]="sidebarMode"
                                [declarations]="currentDeclarations"
                                [selectedAlias]="selectedAlias"
                                [declarationsExist]="currentDeclarationsExist"
                                [stepBindings]="selectedStepBindings"
                                [executionOverlayActive]="!isOnSubFlow"
                                [calledDeclarations]="calledDeclarationsForSelectedStep"
                                [unresolvedCalledAliases]="unresolvedCalledAliasesForSelectedStep"
                                [dynamicCalls]="dynamicCallsForSelectedStep"
                                [expanded]="false"
                                (modeChange)="onSidebarModeChange($event)"
                                (close)="closeStepDetail()"
                                (aliasSelected)="onAliasSelected($event)"
                                (openLogic)="onOpenLogic($event)"
                                (inlineDeclarationClicked)="onInlineDeclarationClicked($event.decl, $event.event)"
                                (inlineDeclarationAuxClicked)="onInlineDeclarationAuxClicked($event.decl, $event.event)">
                            </mvs-lg-flow-step-detail-sidebar>
                        }

                        <!-- TM-1000001563: Ausführungsbaum overlay — floats
                             top-right of the canvas, mirroring the live
                             test-runner's pattern. The wrapping div stops
                             mouse events from bubbling to the canvas's
                             pan/drag handlers (which otherwise hijack
                             clicks on the tree's whitespace). Wrapper class
                             `lg-tree-overlay` lives only for positioning;
                             the inner tree component owns its visual
                             appearance. -->
                        @if (treePanelVisible) {
                            <div class="lg-tree-overlay"
                                 [class.lg-tree-overlay--below-chip]="isOnSubFlow"
                                 (mousedown)="$event.stopPropagation()"
                                 (mouseup)="$event.stopPropagation()"
                                 (click)="$event.stopPropagation()"
                                 (wheel)="$event.stopPropagation()">
                                <mvs-lg-flow-execution-tree-panel
                                    [rootTrace]="treeRootResult"
                                    [childTraceCache]="treeChildCache"
                                    [currentExecutionId]="treeSelectedNodeId"
                                    (executionSelected)="onTreeTraceSelected($event)"
                                    (expandRequested)="onTreeExpandRequested($event)">
                                </mvs-lg-flow-execution-tree-panel>
                            </div>
                        }

                    </div>

                    <!-- TM-1000001563: declaration rail toggled via the
                         canvas toolbar's "Deklarationen" button. Defect 2
                         wiring: forward [selectedAlias] + (aliasSelected) so
                         clicking a declaration card highlights the calling
                         steps on the canvas. (openLogic) navigates to the
                         underlying Logic via the standard right-drawer.
                         Uses the per-level currentDeclarations so the rail
                         reflects whichever sub-flow level the canvas is on. -->
                    @if (declarationsViewOpen) {
                        <div class="flex-shrink-0">
                            <mvs-lg-flow-declaration-rail
                                [declarations]="currentDeclarations"
                                [selectedAlias]="selectedAlias"
                                (aliasSelected)="onAliasSelected($event)"
                                (openLogic)="onOpenLogic($event)">
                            </mvs-lg-flow-declaration-rail>
                        </div>
                    }
                </div>
            </p-panel>
        </div>
    }



    <!-- Per-Logic counter widget -->
    <!-- TM-1000001563: `p-0` added so the counter widget wrapper aligns
         with the other cards above (summary, variable matrix, canvas).
         Without it, the default PrimeFlex `col-12` gutter (0.5rem each
         side) made the widget's table render visibly narrower than the
         three cards. -->
    <div class="col-12 p-0">
        @if (counterWidget) {
            <mvs-widget [widgetData]="counterWidget"></mvs-widget>
        }
    </div>

</div>
